/** @format */

import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Skeleton } from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { apiPresenceCheck } from "../../../api/apiPresences";
import { API_ENDPOINTS } from "../../../constants/api";
import { actionPresenceCreateRequest } from "../../../redux-modules/actions/actionsPresences";
import { actionPresenceTypeListRequest } from "../../../redux-modules/actions/actionsPresenceTypes";
import { TChiCe, THome, THomeDaGestire } from "../../../redux-modules/declarations/maps/home";
import { TPresence } from "../../../redux-modules/declarations/maps/presences";
import { useGenericAlert } from "../../../redux-modules/hooks/useGenericAlert";
import {
  selectorIsGestioneTimebankEnabled,
  selectorIsPresenceInApp,
  selectorLoggedTenance,
  selectorLoggedUser,
} from "../../../redux-modules/selectors/selectorsAuth";
import { selectorJustificativeList } from "../../../redux-modules/selectors/selectorsJustificatives";
import { selectorPresenceTypeList } from "../../../redux-modules/selectors/selectorsPresenceTypes";
import {
  selectorPresenceUpdated,
  selectorRequestListUpdated,
} from "../../../redux-modules/selectors/selectorsUI";
import { usePermissionForRoute } from "../../../utils/permissions/routeToPermissionMap";
import { Routes } from "../../../utils/Routes";
import { showNotification } from "../../../utils/showNotification";
import UITransition from "../../ui/Transition";
import { useLettura } from "../ViewPresenze/usePresenze";
import {
  adessoLocale,
  dataLunga,
  leggiPreferenzeHome,
  periodoDaGestire,
  salvaPreferenzeHome,
  saluto,
} from "./format";
import { ChiCe, TMomento } from "./partials/ChiCe";
import { Contatori } from "./partials/Contatori";
import { DocumentiComunicazioni } from "./partials/DocumentiComunicazioni";
import { Oggi } from "./partials/Oggi";
import { Richieste } from "./partials/Richieste";
import { RigaDaGestire } from "./partials/RigaDaGestire";
import { Settimana } from "./partials/Settimana";
import { Timbratura } from "./partials/Timbratura";
import { HomeStile, Intestazione, Pagina } from "./styles";

/** Con il QR la timbratura arriva dall'iPad: finché il codice è a video si controlla ogni 5 secondi. */
const CONTROLLO_QR = 5000;

const NESSUN_PARAMETRO = {};

/** Differenza massima tra l'ora del browser e quella del server prima di usare quella del server. */
const SCARTO_TOLLERATO = 120000;

/**
 * Home (variante B). Letture:
 *  - GET /home: giornata e settimana, contatori, richieste, documenti, comunicazioni, cosa si vede;
 *  - GET /presences/check/{id}: presenza aperta, come la Home precedente (chiude anche quelle oltre 13 ore);
 *  - GET /home/da-gestire: solo per i responsabili, in parallelo;
 *  - GET /shift-plannings/whos-there: "Chi c'è", solo per i responsabili con Group/Canshowpresencetoday,
 *    ogni minuto per "Adesso" (date e time correnti) e "Oggi" (date).
 * La timbratura usa GET /shift_plannings/next all'apertura della finestra e POST /presences.
 */
export const ViewHome = (): JSX.Element => {
  const dispatch = useDispatch();
  const Alerta = useGenericAlert(6000);
  const utente = useSelector(selectorLoggedUser);
  const tenance = useSelector(selectorLoggedTenance);
  const inApp = useSelector(selectorIsPresenceInApp);
  const timebankAbilitata = useSelector(selectorIsGestioneTimebankEnabled);
  const tipiPresenza = useSelector(selectorPresenceTypeList);
  const giustificativi = useSelector(selectorJustificativeList);
  const presenzaAggiornata = useSelector(selectorPresenceUpdated);
  const richiestaAggiornata = useSelector(selectorRequestListUpdated);
  const vedeTurni = usePermissionForRoute(Routes.shifts);

  const home = useLettura<THome>(
    API_ENDPOINTS.HOME,
    NESSUN_PARAMETRO,
    !!utente,
    false,
    "Impossibile caricare la Home",
  );
  const dati = home.dati;
  const ricaricaHome = home.ricarica;

  // "adesso" per la linea, le durate e i testi relativi: l'ora del browser, riallineata a quella
  // del server se le due differiscono di più di 2 minuti (fuso o orologio del computer sbagliati)
  const scarto = useRef(0);
  const [orologio, setOrologio] = useState(moment());
  useEffect(() => {
    if (!dati) return;
    const differenza = moment(dati.adesso, "YYYY-MM-DD HH:mm").valueOf() - Date.now();
    scarto.current = Math.abs(differenza) > SCARTO_TOLLERATO ? differenza : 0;
    setOrologio(moment(Date.now() + scarto.current));
  }, [dati]);
  useEffect(() => {
    const timer = window.setInterval(() => setOrologio(moment(Date.now() + scarto.current)), 30000);
    return (): void => window.clearInterval(timer);
  }, []);
  const adesso = adessoLocale(orologio);
  const oggi = orologio.format("YYYY-MM-DD");

  // presenza aperta: stessa lettura della Home precedente
  const [aperta, setAperta] = useState<TPresence | null>(null);
  const leggiAperta = useCallback((): Promise<TPresence | null> => {
    if (!utente) return Promise.resolve(null);
    return apiPresenceCheck(`${utente.id}`)
      .then(r => {
        const elenco = ((r.data as { data?: TPresence[] })?.data || []) as TPresence[];
        const prima = elenco.length ? elenco[0] : null;
        setAperta(prima);
        return prima;
      })
      .catch(() => null);
  }, [utente]);

  useEffect(() => {
    dispatch(actionPresenceTypeListRequest());
    leggiAperta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // dopo una timbratura dal web o una richiesta: si rileggono la presenza aperta e la Home
  useEffect(() => {
    if (!presenzaAggiornata) return;
    leggiAperta();
    ricaricaHome();
  }, [presenzaAggiornata, leggiAperta, ricaricaHome]);

  // ------------------------------------------------------------------ Da gestire
  const periodo = useMemo(() => periodoDaGestire(moment()), []);
  const daGestire = useLettura<THomeDaGestire>(
    API_ENDPOINTS.HOME_DA_GESTIRE,
    useMemo(() => ({ dal: periodo.dal, al: periodo.al }), [periodo]),
    !!dati?.vede.da_gestire,
    false,
    "Impossibile leggere cosa c'è da gestire",
  );
  const ricaricaDaGestire = daGestire.ricarica;

  useEffect(() => {
    if (!richiestaAggiornata) return;
    ricaricaHome();
    ricaricaDaGestire();
  }, [richiestaAggiornata, ricaricaHome, ricaricaDaGestire]);

  // ------------------------------------------------------------------ Chi c'è
  const [momento, setMomento] = useState<TMomento>("adesso");
  const [gruppo, setGruppo] = useState(() => leggiPreferenzeHome().gruppo);
  // "Adesso" chiede data e ora correnti (modalità planned con time): senza, whos-there legge anche
  // le timbrature di ieri rimaste aperte. L'ora si calcola a ogni chiamata, quindi la lettura
  // al minuto resta silenziosa; "Oggi" e "Domani" chiedono solo la data.
  const parametriChiCe = useMemo(
    (): Record<string, string> =>
      momento === "adesso"
        ? { momento: "adesso" }
        : { date: momento === "oggi" ? oggi : moment(oggi).add(1, "day").format("YYYY-MM-DD") },
    [momento, oggi],
  );
  const parametriAdesso = useCallback((p: Record<string, string>): Record<string, string> => {
    if (p.momento !== "adesso") return p;
    const ora = moment(Date.now() + scarto.current);
    return { date: ora.format("YYYY-MM-DD"), time: ora.format("HH:mm") };
  }, []);
  const chiCe = useLettura<TChiCe>(
    API_ENDPOINTS.WHOS_THERE,
    parametriChiCe,
    !!dati?.vede.chi_ce,
    momento !== "domani",
    "Impossibile leggere chi c'è",
    parametriAdesso,
  );
  const cambiaGruppo = (g: string): void => {
    setGruppo(g);
    salvaPreferenzeHome({ gruppo: g });
  };
  const gruppiUtente = useMemo(() => (utente?.groups || []).map(g => String(g.id)), [utente]);

  // ------------------------------------------------------------------ timbratura
  const [timbratura, setTimbratura] = useState(false);
  const apertaAllInizio = useRef<string | null>(null);

  const apriTimbratura = (): void => {
    apertaAllInizio.current = aperta ? String(aperta.id) : null;
    setTimbratura(true);
  };

  // con il QR: quando l'iPad registra la timbratura la finestra si chiude e la Home si aggiorna
  useEffect(() => {
    if (!timbratura || inApp) return undefined;
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      leggiAperta().then(nuova => {
        const id = nuova ? String(nuova.id) : null;
        if (id === apertaAllInizio.current) return;
        apertaAllInizio.current = id;
        setTimbratura(false);
        showNotification(
          "success",
          "Timbratura",
          nuova ? "Ingresso registrato" : "Uscita registrata",
        );
        ricaricaHome();
      });
    }, CONTROLLO_QR);
    return (): void => window.clearInterval(timer);
  }, [timbratura, inApp, leggiAperta, ricaricaHome]);

  const chiudiDalWeb = (): void => {
    if (!aperta || !utente) return;
    dispatch(
      actionPresenceCreateRequest({
        generate_date: moment().format("YYYY-MM-DD HH:mm:ss"),
        user_id: utente.id,
        presence_type_id: aperta.presence_type_id,
        origin: "web",
      }),
    );
  };

  if (!utente) return <>{Alerta}</>;

  const giornoOggi = dati ? dati.settimana.find(g => g.data === dati.oggi) || null : null;
  const reparto = dati?.utente.reparto;
  const sede = dati?.utente.sede;

  return (
    <UITransition>
      {Alerta}
      <Pagina>
        <Intestazione>
          <div>
            <h1 data-cy="page-title">
              {saluto(orologio)}, {dati?.utente.nome || utente.name}
            </h1>
            <div className="sottotitolo">
              {[dataLunga(oggi), reparto, sede].filter(Boolean).join(" · ")}
            </div>
          </div>
        </Intestazione>

        <HomeStile>
          {home.errore && (
            <Alert
              type="error"
              showIcon
              message={home.errore}
              action={
                <Button size="small" icon={<ReloadOutlined />} onClick={ricaricaHome}>
                  Riprova
                </Button>
              }
            />
          )}

          {!dati && home.caricamento && (
            <>
              <section className="scheda">
                <Skeleton active paragraph={{ rows: 2 }} />
              </section>
              <section className="scheda">
                <Skeleton active paragraph={{ rows: 5 }} />
              </section>
            </>
          )}

          {dati && (
            <>
              <Oggi
                home={dati}
                giorno={giornoOggi}
                aperta={aperta}
                adesso={adesso}
                inApp={inApp}
                onTimbra={apriTimbratura}
                onChiudiDalWeb={chiudiDalWeb}
              />

              {dati.vede.da_gestire && (
                <RigaDaGestire
                  lettura={daGestire}
                  periodo={periodo}
                  oggi={oggi}
                  turniSettimanali={tenance?.view_weekly_shifts !== false}
                />
              )}

              {dati.vede.chi_ce ? (
                <ChiCe
                  lettura={chiCe}
                  momento={momento}
                  onMomento={setMomento}
                  gruppo={gruppo}
                  onGruppo={cambiaGruppo}
                  cambiaGruppo={dati.vede.cambia_gruppo}
                  gruppiUtente={gruppiUtente}
                  utenteId={utente.id}
                  tipoStraordinarioId={dati.tipo_presenza_straordinario_id}
                  adesso={adesso}
                />
              ) : (
                <Settimana
                  giorni={dati.settimana}
                  oggi={dati.oggi}
                  turni={vedeTurni && tenance?.view_weekly_shifts !== false ? Routes.shifts : null}
                />
              )}

              <div className="tre">
                <Contatori contatori={dati.contatori} />
                <Richieste
                  richieste={dati.richieste}
                  oggi={oggi}
                  tipi={giustificativi.data || []}
                />
                <DocumentiComunicazioni
                  documenti={dati.documenti}
                  comunicazioni={dati.comunicazioni}
                  oggi={oggi}
                  utenteId={String(utente.id)}
                  onLetto={ricaricaHome}
                />
              </div>
            </>
          )}
        </HomeStile>
      </Pagina>

      <Timbratura
        visibile={timbratura}
        inApp={inApp}
        timebankAbilitata={timebankAbilitata}
        userId={utente.id}
        tipiPresenza={tipiPresenza}
        aperta={aperta}
        onChiudi={(): void => setTimbratura(false)}
      />
    </UITransition>
  );
};
